import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import { myDashboardKeys } from '../../my-dashboard/hooks/use-my-dashboard';
import { studentDirectoryKeys } from '../../student-directory/hooks/use-student-directory';
import { studentsApi } from '../students';

/** A status change moves the child between lists, registers, the directory and the For you queue. */
function invalidateEnrolment(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['admin', 'sand-dollars'] });
  void qc.invalidateQueries({ queryKey: studentDirectoryKeys.all });
  void qc.invalidateQueries({ queryKey: myDashboardKeys.all });
}

export function useStartStudentTrialMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; trialStartDate: string; trialEndDate: string }) =>
      studentsApi.startTrial(id, data),
    onSuccess: () => invalidateEnrolment(qc),
  });
}

export function useExtendStudentTrialMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, trialEndDate }: { id: string; trialEndDate: string }) =>
      studentsApi.extendTrial(id, trialEndDate),
    onSuccess: () => invalidateEnrolment(qc),
  });
}

export function useEnrolStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof studentsApi.enrol>[1] }) =>
      studentsApi.enrol(id, data),
    onSuccess: (_result, { data }) => {
      invalidateEnrolment(qc);
      if (data.createDraftInvoice) void qc.invalidateQueries({ queryKey: ['tuition'] });
    },
  });
}

export function useChangeStudentStartDateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, startDate }: { id: string; startDate: string }) => studentsApi.changeStartDate(id, startDate),
    onSuccess: () => invalidateEnrolment(qc),
  });
}

export function useMarkStudentLeftMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => studentsApi.markLeft(id),
    onSuccess: () => invalidateEnrolment(qc),
  });
}
